import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import type { CodeListItem } from '@models/codelist';
import type { CombinationPriceGrid } from '@models/combination-prices';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/confirmation.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { finalize, firstValueFrom, type Observable } from 'rxjs';
import { AddPriceListButtonComponent } from '../../pricing/price-lists/add-price-list-button/add-price-list-button.component';
import { PriceListSelectComponent } from '../price-list-select/price-list-select.component';
import { parsePriceInput } from '../pricing-grid/parse-price-input';
import { type PriceField, resolvePriceListId, withPriceListInOrder } from '../pricing-grid/pricing-grid.model';
import { parseGridKey } from '../pricing-grid/pricing-grid.store';
import type {
  CombinationCoords,
  CombinationPricingUnitType,
  CopySource,
  GridAxis,
  WaterPriceListOption,
} from './combination-pricing.model';
import { CombinationPricingDataService } from './combination-pricing-data.service';
import { CombinationPricingGridStore } from './combination-pricing-grid.store';

export type { CombinationPricingUnitType } from './combination-pricing.model';

/**
 * "Pricing per combination" section for an accommodation or fishing spot (CS-285). Lets staff enter a
 * purchase + selling price per (anglers × non-anglers) for each season × stay type, within one price
 * list. Both unit types share an identical grid; `unitType` only picks which endpoint is called.
 *
 * The matrix axes track the parent form's *live* capacity controls (spots / minimum fishermen /
 * co-travelers), so rows and columns appear and disappear as those inputs change — no save needed.
 * Shrinking spots or co-travelers past a filled-in cell first asks for confirmation, then drops the
 * now out-of-range cells so the unified form submit reconciles them away in the backend.
 *
 * Fetch granularity is one blob per price list: selecting a price list loads its full grid (all
 * seasons × stay types × cells); switching season or stay type only re-filters in memory. Saving is
 * driven by the parent form — {@link save} returns the PUT observable for the whole price-list grid
 * (the API reconciles to minimal writes), so all cells are held in memory.
 *
 * A CRM user can copy a swim's or accommodation's full price set to another swim/accommodation:
 * "Copy pricing from…" pulls another unit's grid for the *currently-selected* price list into the
 * in-memory map (see {@link copyFrom}) — past/future price lists are untouched — and "Reset grid"
 * clears the visible season × stay-type matrix ({@link resetVisibleGrid}). Both only mutate memory;
 * the parent form's Save persists them.
 *
 * The editable cell grid itself (all cell math + dirty/error state) lives in {@link CombinationPricingGridStore},
 * and all HTTP lives in {@link CombinationPricingDataService}; this component orchestrates the UI.
 */
@Component({
  selector: 'app-combination-pricing',
  templateUrl: './combination-pricing.component.html',
  styleUrl: './combination-pricing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CombinationPricingGridStore],
  imports: [
    DecimalPipe,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatSelectModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    AddPriceListButtonComponent,
    PriceListSelectComponent,
    TranslatePipe,
  ],
})
export class CombinationPricingComponent implements OnInit {
  readonly waterId = input.required<number>();
  readonly unitId = input.required<number>();
  readonly unitType = input.required<CombinationPricingUnitType>();

  // Capacity controls owned by the parent form; the matrix axes follow their live values, and a
  // rejected shrink is reverted straight on the control.
  readonly spotsControl = input.required<FormControl<number | null>>(); // max anglers (grid rows)
  readonly minimumFishermenControl = input.required<FormControl<number | null>>(); // min anglers
  readonly coTravelersControl = input.required<FormControl<number | null>>(); // max non-anglers (columns)

  private readonly gridStore = inject(CombinationPricingGridStore);
  private readonly dataService = inject(CombinationPricingDataService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  // Dimension 1 — price lists for this water; switching is guarded by PriceListSelectComponent.
  readonly priceLists = signal<WaterPriceListOption[]>([]);
  readonly selectedPriceListId = signal<number | null>(null);

  // Other units of this water whose pricing can be copied in (the current unit is excluded).
  readonly copySources = signal<CopySource[]>([]);

  // Dimension 2 — seasons this water is priced by, labelled and ordered by the API.
  readonly seasonOptions = signal<CodeListItem[]>([]);
  private readonly selectedSeasonId = signal<number | null>(null);
  readonly activeSeasonId = computed(() => this.selectedSeasonId() ?? this.seasonOptions()[0]?.id ?? null);

  // Dimension 3 — stay types as tabs. Per-water (from the water's week/midweek/weekend flags), matching
  // what the pricing API validates against; loaded with the rest of the initial data.
  readonly stayTypes = signal<CodeListItem[]>([]);
  private readonly selectedStayTypeId = signal<number | null>(null);
  readonly activeStayTypeId = computed(() => this.selectedStayTypeId() ?? this.stayTypes()[0]?.id ?? null);
  readonly activeStayTypeIndex = computed(() => {
    const idx = this.stayTypes().findIndex((t) => t.id === this.activeStayTypeId());
    return idx >= 0 ? idx : 0;
  });

  // The loaded grid header (price-list info); the editable cell map lives in the grid store.
  protected readonly grid = signal<CombinationPriceGrid | null>(null);

  readonly isLoading = signal(false);

  // Edit + validation state is owned by the grid store; re-exposed here for the template.
  readonly dirty = this.gridStore.dirty;
  readonly showErrors = this.gridStore.showErrors;
  readonly hasPartialCells = computed(() => this.gridStore.partialKeys().length > 0);
  readonly hasOutOfRangeCells = computed(() => this.gridStore.outOfRangeKeys().length > 0);

  // Dimensions 4 & 5 — grid axes, mirrored from the parent form's capacity controls into the store,
  // which needs them to decide which cells may exist and which may be saved.
  private readonly bounds = this.gridStore.bounds;

  readonly anglersRange = computed(() => {
    const { minAnglers, maxAnglers } = this.bounds();
    if (maxAnglers == null) return [];
    return range(minAnglers, maxAnglers);
  });
  readonly nonAnglersRange = computed(() => {
    const { maxNonAnglers } = this.bounds();
    if (maxNonAnglers == null) return [];
    return range(0, maxNonAnglers);
  });

  readonly hasPriceLists = computed(() => this.priceLists().length > 0);
  readonly hasSeasons = computed(() => this.seasonOptions().length > 0);
  readonly hasStayTypes = computed(() => this.stayTypes().length > 0);
  readonly capacityMissing = computed(() => {
    const { maxAnglers, maxNonAnglers } = this.bounds();
    return maxAnglers == null || maxNonAnglers == null;
  });
  // Both capacities are set but the lowest anglers row is above spots, so anglersRange is empty and the
  // grid can't render — surface why instead of leaving a blank void under the tabs. Since minAnglers is
  // normalised to at least 1, this also covers a unit with zero spots and no minimum of its own.
  readonly minAnglersExceedsSpots = computed(() => {
    const { minAnglers, maxAnglers } = this.bounds();
    return maxAnglers != null && minAnglers > maxAnglers;
  });
  readonly canRenderGrid = computed(
    () =>
      this.grid() != null &&
      !this.capacityMissing() &&
      this.hasSeasons() &&
      this.hasStayTypes() &&
      this.activeStayTypeId() != null &&
      this.anglersRange().length > 0 &&
      this.nonAnglersRange().length > 0,
  );

  ngOnInit(): void {
    // Seed the axes from the (already patched) form values, then keep them in sync live.
    this.gridStore.setMax('anglers', this.spotsControl().value);
    this.gridStore.setMinAnglers(this.minimumFishermenControl().value);
    this.gridStore.setMax('nonAnglers', this.coTravelersControl().value);

    this.spotsControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.onMaxAxisChange('anglers', this.spotsControl(), value));
    this.coTravelersControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.onMaxAxisChange('nonAnglers', this.coTravelersControl(), value));
    // Minimum fishermen only shifts the lower row bound (display); it never deletes cells.
    this.minimumFishermenControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.gridStore.setMinAnglers(value));

    this.loadPriceListsAndSeasons();
  }

  private loadPriceListsAndSeasons(): void {
    this.isLoading.set(true);
    this.dataService
      .loadInitial(this.waterId(), this.unitType(), this.unitId())
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => ({ priceLists: [], seasons: [], stayTypes: [], copySources: [] }),
          this.translate.instant('waters.combination-prices.load-failed'),
        ),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe(({ priceLists, seasons, stayTypes, copySources }) => {
        this.priceLists.set(priceLists);
        this.seasonOptions.set(seasons);
        this.stayTypes.set(stayTypes);
        this.copySources.set(copySources);
        // A previously-picked stay type may not be among the water's own; drop it so the tab index and
        // the active id can't disagree (activeStayTypeIndex falls back to 0 on a miss).
        if (!stayTypes.some((t) => t.id === this.selectedStayTypeId())) this.selectedStayTypeId.set(null);

        // Opens on the price list in effect today rather than the newest, which may be staged for a
        // season that has not started. Only ever a first load today, but resolved the same way the
        // exclusive grid resolves its own so the two cannot drift apart.
        const next = resolvePriceListId(priceLists, this.selectedPriceListId());
        if (next != null) this.loadGrid(next);
      });
  }

  /**
   * Adopt a price list created from the button beside the dropdown, and price it straight away — an
   * empty grid to fill is why it was added. The button has already had any unsaved cell edits confirmed
   * away, so switching here does not need to ask again.
   */
  protected onPriceListCreated(created: WaterPriceListOption): void {
    this.priceLists.update((priceLists) => withPriceListInOrder(priceLists, created));
    this.loadGrid(created.id);
  }

  /** Load one price list's grid. Bound to the price-list dropdown, which confirms a dirty switch first. */
  protected loadGrid(priceListId: number): void {
    this.selectedPriceListId.set(priceListId);

    // Create mode: the unit is not saved yet (id 0), so there is no grid to fetch. Start empty; the
    // cells the user enters here are PUT together with the newly-created unit's id on submit.
    if (this.unitId() === 0) {
      const priceList = this.priceLists().find((p) => p.id === priceListId);
      this.grid.set({
        unitId: 0,
        unitTitle: null,
        priceListId,
        priceListName: priceList?.name ?? '',
        priceListStartDate: priceList?.startDate ?? '',
        spots: null,
        coTravelers: null,
        minimumFishermen: null,
        persons: null,
        cells: [],
      });
      this.gridStore.load([]);
      return;
    }

    this.isLoading.set(true);
    this.dataService
      .getGrid(this.unitType(), this.waterId(), this.unitId(), priceListId)
      .pipe(
        showGenericErrorToast(
          this.toastService,
          () => null,
          this.translate.instant('waters.combination-prices.load-failed'),
        ),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((grid) => {
        this.grid.set(grid);
        this.gridStore.load(grid?.cells ?? []);
      });
  }

  onSeasonChange(seasonId: number): void {
    this.selectedSeasonId.set(seasonId);
  }

  onStayTypeTabChange(index: number): void {
    const stayType = this.stayTypes()[index];
    if (stayType) this.selectedStayTypeId.set(stayType.id);
  }

  // --- Axis changes (spots / co-travelers) ---

  /**
   * Apply a max-axis change from the parent form. Growing, clearing, or a shrink that strands no
   * filled cells applies silently; a shrink that would drop filled cells asks first and, on confirm,
   * prunes them (on cancel, the control is reverted to its previous value).
   */
  private onMaxAxisChange(axis: GridAxis, control: FormControl<number | null>, newValue: number | null): void {
    const bounds = this.bounds();
    const previous = axis === 'anglers' ? bounds.maxAnglers : bounds.maxNonAnglers;
    if (newValue == null || previous == null || newValue >= previous) {
      this.gridStore.setMax(axis, newValue);
      return;
    }

    const lostKeys = this.gridStore.filledCellsBeyond(axis, newValue);
    if (lostKeys.length === 0) {
      this.gridStore.setMax(axis, newValue);
      return;
    }

    void this.confirmShrink(axis, control, previous, newValue, lostKeys);
  }

  private async confirmShrink(
    axis: GridAxis,
    control: FormControl<number | null>,
    previous: number,
    newValue: number,
    lostKeys: string[],
  ): Promise<void> {
    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.combination-prices.shrink-title'),
      prompt: this.translate.instant('waters.combination-prices.shrink-prompt'),
    });
    if (!confirmed) {
      control.setValue(previous, { emitEvent: false });
      return;
    }
    this.gridStore.setMax(axis, newValue);
    this.gridStore.prune(lostKeys);
  }

  // --- Cell editing (delegated to the grid store, keyed by the active season + stay type) ---

  getPurchase(anglers: number, nonAnglers: number): number | null {
    const coords = this.coords(anglers, nonAnglers);
    return coords == null ? null : this.gridStore.getPurchase(coords);
  }

  getSelling(anglers: number, nonAnglers: number): number | null {
    const coords = this.coords(anglers, nonAnglers);
    return coords == null ? null : this.gridStore.getSelling(coords);
  }

  margin(anglers: number, nonAnglers: number): number | null {
    const coords = this.coords(anglers, nonAnglers);
    return coords == null ? null : this.gridStore.margin(coords);
  }

  isCellInvalid(anglers: number, nonAnglers: number): boolean {
    const coords = this.coords(anglers, nonAnglers);
    return coords != null && this.gridStore.isCellInvalid(coords);
  }

  /** Half-filled specifically — an out-of-range cell is outlined too, but needs a different message. */
  isCellPartial(anglers: number, nonAnglers: number): boolean {
    const coords = this.coords(anglers, nonAnglers);
    return coords != null && this.gridStore.isCellPartial(coords);
  }

  setPurchase(anglers: number, nonAnglers: number, event: Event): void {
    this.setPrice(anglers, nonAnglers, 'purchasePrice', parsePriceInput(event));
  }

  setSelling(anglers: number, nonAnglers: number, event: Event): void {
    this.setPrice(anglers, nonAnglers, 'sellingPrice', parsePriceInput(event));
  }

  private setPrice(anglers: number, nonAnglers: number, field: PriceField, value: number | null): void {
    const coords = this.coords(anglers, nonAnglers);
    if (coords == null) return;
    this.gridStore.setPrice(coords, field, value);
  }

  /** The cell's full coordinates under the active season + stay type, or null while either is unresolved. */
  private coords(anglers: number, nonAnglers: number): CombinationCoords | null {
    const seasonId = this.activeSeasonId();
    const stayTypeId = this.activeStayTypeId();
    if (seasonId == null || stayTypeId == null) return null;
    return [seasonId, stayTypeId, anglers, nonAnglers];
  }

  // --- Copy & reset ---

  /**
   * Copy another unit's pricing for the *currently-selected* price list into this grid. Fetches the
   * source unit's grid, confirms (noting the current-price-list-only scope, plus an overwrite warning
   * when this grid already holds prices), then replaces the in-memory cells (dropping any that exceed
   * the current capacity). Only memory changes — the parent form's Save persists it.
   */
  async copyFrom(source: CopySource): Promise<void> {
    const priceListId = this.selectedPriceListId();
    if (priceListId == null) return;

    this.isLoading.set(true);
    const sourceGrid = await firstValueFrom(
      this.dataService.getGrid(source.type, this.waterId(), source.id, priceListId).pipe(
        showGenericErrorToast(
          this.toastService,
          () => null,
          this.translate.instant('waters.combination-prices.load-failed'),
        ),
        finalize(() => this.isLoading.set(false)),
      ),
    );
    if (!sourceGrid) return;

    if (sourceGrid.cells.length === 0) {
      this.toastService.show(this.translate.instant('waters.combination-prices.copy-empty', { source: source.label }));
      return;
    }

    const priceListName = this.priceLists().find((p) => p.id === priceListId)?.name ?? '';
    let prompt = this.translate.instant('waters.combination-prices.copy-confirm-prompt', {
      source: source.label,
      priceList: priceListName,
    });
    if (this.gridStore.hasFilledCells()) {
      prompt += ` ${this.translate.instant('waters.combination-prices.copy-confirm-overwrite')}`;
    }

    const confirmed = await this.confirmation.open({
      title: this.translate.instant('waters.combination-prices.copy-confirm-title'),
      prompt,
    });
    if (!confirmed) return;

    // Keep only cells that fit the current capacity; out-of-range cells would never render or save.
    const { copied, skipped } = this.gridStore.replaceFitting(sourceGrid.cells);

    if (skipped > 0) {
      // The skipped-count message is long — leave it up longer so it can be read in full.
      this.toastService.showSuccess(
        this.translate.instant('waters.combination-prices.copy-success-partial', {
          copied,
          skipped,
        }),
        6000,
      );
    } else {
      this.toastService.showSuccess(this.translate.instant('waters.combination-prices.copy-success'));
    }
  }

  /**
   * Clear only the visible anglers × non-anglers matrix (the current season × stay type); other
   * seasons and stay types are untouched. Confirms first only when that visible grid holds prices.
   */
  async resetVisibleGrid(): Promise<void> {
    const seasonId = this.activeSeasonId();
    const stayTypeId = this.activeStayTypeId();
    if (seasonId == null || stayTypeId == null) return;

    const { keys, hasData } = this.gridStore.visibleKeys(seasonId, stayTypeId);
    if (keys.length === 0) return;

    if (hasData) {
      const confirmed = await this.confirmation.open({
        title: this.translate.instant('waters.combination-prices.reset-confirm-title'),
        prompt: this.translate.instant('waters.combination-prices.reset-confirm-prompt', {
          season: this.activeSeasonName(),
          stayType: this.activeStayTypeName(),
        }),
      });
      if (!confirmed) return;
    }

    this.gridStore.clear(keys);
  }

  private activeSeasonName(): string {
    return this.seasonOptions().find((s) => s.id === this.activeSeasonId())?.name ?? '';
  }

  private activeStayTypeName(): string {
    return this.stayTypes().find((t) => t.id === this.activeStayTypeId())?.name ?? '';
  }

  /**
   * Validate before the parent submits: every cell must have both prices or neither, and no price may
   * fall outside the range the API accepts (a wider value is a hard 400 whose body never reaches the
   * user). Returns true when valid; otherwise reveals the inline errors, jumps the view to the first
   * offending cell's season + stay type, and returns false so the parent aborts the whole save.
   */
  validate(): boolean {
    const offending = [...this.gridStore.partialKeys(), ...this.gridStore.outOfRangeKeys()];
    if (offending.length === 0) {
      this.gridStore.showErrors.set(false);
      return true;
    }
    this.gridStore.showErrors.set(true);
    const [seasonId, stayTypeId] = parseGridKey(offending[0]);
    this.selectedSeasonId.set(seasonId);
    this.selectedStayTypeId.set(stayTypeId);
    return false;
  }

  /**
   * Build the PUT for the currently-loaded price-list grid, or `null` when there is nothing to save.
   * The parent form chains this into its own submit. In create mode the unit has no id until its own
   * save returns, so the parent passes the freshly-created id via `overrideUnitId`. Cells outside the
   * current min/max bounds are dropped defensively so a stray out-of-range cell can never be persisted.
   *
   * Nothing is sent unless the grid actually rendered. The store holds every cell the GET returned, but
   * when the grid can't render — the water offers no seasons or stay types, or capacity is unset — the
   * user was shown no grid at all, and a whole-grid PUT of cells they never saw would be reconciled
   * against dimensions the water no longer has. Skipping the PUT leaves the stored grid as it is.
   */
  save(overrideUnitId?: number): Observable<CombinationPriceGrid> | null {
    const priceListId = this.selectedPriceListId();
    if (!this.canRenderGrid() || priceListId == null) return null;

    const cells = this.gridStore.toPayload();

    const unitId = overrideUnitId ?? this.unitId();
    if (unitId === 0) return null; // no real unit to attach prices to
    // Create mode with no prices entered: nothing to write, so skip the PUT entirely.
    if (this.unitId() === 0 && cells.length === 0) return null;

    return this.dataService.saveGrid(this.unitType(), this.waterId(), unitId, priceListId, { cells });
  }
}

function range(lo: number, hi: number): number[] {
  if (hi < lo) return [];
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}
